<script setup>
import {
  ref,
  computed,
  watch,
  onUnmounted,
  onMounted,
  defineExpose,
  defineEmits,
  nextTick,
} from 'vue';
import BScroll from 'better-scroll';
import { useIns, useStore } from '../../../utils/v2_helper/common';

const WIDTH = '100px';
const WIDTH_S = '40px';
const app = useIns();
const store = useStore();
const emits = defineEmits(['onTap']);
let bs = null;

const contentRef = ref();
const listRef = ref();
const currentIndex = ref(0);
const small = ref(false);
const show = ref(false);

const categories = computed(() => store.state.prefetch?.categories || []);

function scrollTo(index) {
  currentIndex.value = index;
  const children = contentRef.value?.children || [];
  const el = children[index];
  if (!el) return;
  if (index !== 0) return bs?.scrollToElement(el, 300, true);
  bs.scrollTo(0, 0, 300);
}
function onTap(index, it) {
  scrollTo(index);
  emits('onTap', it);
}
async function initBS() {
  await nextTick();
  if (!bs) {
    bs = new BScroll('.tabbar-wrap', { scrollX: true, probeType: 3 });
    bs.on('scroll', (position) => {
      small.value = position.x < -10;
    });
  } else {
    bs.refresh();
  }
}

watch(() => categories.value.length, initBS);
watch(show, async (v) => {
  await nextTick();
  if (v) listRef.value?.children?.[currentIndex.value]?.scrollIntoView();
});
defineExpose({ scrollTo });
onMounted(() => initBS());
onUnmounted(() => bs?.destroy());
</script>

<template>
  <div class="tabbar">
    <div class="tabbar-cate" @click="show = true">
      <span :class="['core', { small }]">
        <i class="iconfont dm-view-grid" />
        <span bold class="text">{{ $t('list.category') }}</span>
      </span>
    </div>
    <!--ScrollContainer-->
    <section class="tabbar-wrap">
      <div ref="contentRef" class="content">
        <button
          v-for="(it, index) in categories"
          :key="it.id"
          :class="['item', { active: index == currentIndex }]"
          @click="onTap(index, it)"
        >
          {{ it.name }}
        </button>
      </div>
    </section>
    <div class="tabbar-search" @click="$router.push(`/${storeUrl}/search`)">
      <i class="iconfont dm-Search1" />
    </div>
    <!--Dialog-->
    <q-dialog v-model="show" position="bottom">
      <div class="catelist">
        <div bold class="catelist-head">
          {{ $t('list.category') }}
          <i class="iconfont dm-close" @click="show = false" @click.stop />
        </div>
        <div ref="listRef" class="catelist-list">
          <div
            v-for="(it, i) in categories"
            :key="it.id"
            v-close-popup
            :class="['item', { active: currentIndex == i }]"
            @click="onTap(i, it), (show = false)"
          >
            {{ it.name }}
            <div class="item-count">{{ it.count }}</div>
          </div>
        </div>
      </div>
    </q-dialog>
  </div>
</template>

<style lang="scss" scoped>
.tabbar {
  background: #fff;
  height: 48px;
  position: relative;
  width: 100%;
  overflow: hidden;
  font-size: 12px;
  margin-bottom: 1px;
  margin-top: 8px;
  ::-webkit-scrollbar {
    width: 4px;
  }
  &-search {
    position: absolute;
    right: 0;
    top: 0;
    bottom: 0;
    width: 44px;
    line-height: 44px;
    text-align: center;
    background: #fff;
    z-index: 99;
  }
  &-cate {
    z-index: 99;
    background: #fff;
    position: absolute;
    padding: 0 5px 0 12px;
    left: 0;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    .core {
      transition: all 0.3s;
      padding: 0 12px;
      height: 32px;
      width: v-bind(WIDTH);
      border-radius: 9999px;
      background: $tabbarCateBg;
      border: none;
      display: flex;
      align-items: center;
      justify-content: space-between;
      overflow: hidden;
      .iconfont {
        color: $black !important;
      }
      .text {
        transition: all 0.3s;
      }
      &.small {
        .text {
          opacity: 0;
        }
        width: v-bind(WIDTH_S);
      }
    }
  }
  &-wrap {
    width: 100%;
    line-height: 48px;
    .content {
      display: inline-block;
      white-space: nowrap;
      padding: 0 48px 0 100px;
    }
    .item {
      font-weight: bold;
      font-size: 14px;
      display: inline-block;
      white-space: nowrap;
      padding: 0 10px;
      height: 32px;
      line-height: 32px;
      color: $grayFont;
      border-radius: 9999px;
      text-overflow: ellipsis;
      overflow: hidden;
      max-width: calc(100vw - 160px);
      @media screen and (min-width: 768px) {
        max-width: 200px;
      }
      &.active {
        background: $black;
        color: #fff;
      }
      margin-left: 8px;
      &:first-child {
        margin-left: 20px;
      }
    }
  }
}
// dialog
.catelist {
  background: #fff;
  height: 500px;
  width: 100%;
  display: flex;
  flex-direction: column;
  &-head {
    padding: 0 16px;
    height: 48px;
    display: flex;
    align-items: center;
    background: #fff;
    width: 100%;
    z-index: 10;
    .iconfont {
      margin-left: auto;
      font-size: 20px;
    }
  }
  &-list {
    flex: 1;
    overflow: auto;
    .item {
      display: flex;
      padding: 16px;
      position: relative;
      border-bottom: 1px solid $border-lg;
      word-break: break-word;
      .item-count {
        color: $grayFont;
        margin-left: auto;
        font-weight: normal;
        min-width: 40px;
        text-align: right;
      }
      &.active {
        font-weight: bold;
      }
      &.active::before {
        content: '';
        position: absolute;
        top: 0;
        bottom: 0;
        left: 0;
        width: 5px;
        background: $black;
      }
    }
  }
}
</style>
